import React, { useState } from 'react';
import { LocationMarker } from './LocationMarker';
import { LocationCard, LocationData } from './LocationCard';
import { FilterPanel, FilterState } from './FilterPanel';
import { WeatherWidget } from './WeatherWidget';
import { useApp } from '../../context/AppContext';
import { AnimatePresence } from 'framer-motion';
const LOCATIONS: LocationData[] = [{
  id: '1',
  name: 'Emerald Lake',
  type: 'lake',
  rating: 4.8,
  distance: '2.4 mi',
  fishTypes: ['Trout', 'Bass'],
  description: 'Crystal clear mountain lake. Best fished in the morning from the northern shore.',
  regulations: 'Catch and release only',
  image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&q=80&w=800'
}, {
  id: '2',
  name: 'Snake River',
  type: 'river',
  rating: 4.5,
  distance: '5.1 mi',
  fishTypes: ['Salmon', 'Steelhead'],
  description: 'Fast-moving waters perfect for fly fishing. Special permit required during spawning.',
  regulations: 'Barbless hooks required',
  image: 'https://images.unsplash.com/photo-1455582916367-25f75bfc6710?auto=format&fit=crop&q=80&w=800'
}, {
  id: '3',
  name: 'Misty Coast',
  type: 'coast',
  rating: 4.9,
  distance: '12 mi',
  fishTypes: ['Halibut', 'Rockfish'],
  description: 'Rocky coastal point with deep waters. Excellent for surf fishing during high tide.',
  regulations: 'Daily limit: 2',
  image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&q=80&w=800'
}, {
  id: '4',
  name: 'Twin Peaks Pond',
  type: 'lake',
  rating: 4.2,
  distance: '8.3 mi',
  fishTypes: ['Bass', 'Crappie'],
  description: 'Quiet pond surrounded by pine trees. Great for beginners and families.',
  regulations: 'No motorized boats',
  image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&q=80&w=800'
}, {
  id: '5',
  name: 'Crystal Creek',
  type: 'river',
  rating: 4.7,
  distance: '15 mi',
  fishTypes: ['Trout'],
  description: 'Pristine creek with abundant trout. Fly fishing only.',
  regulations: 'Fly fishing only',
  image: 'https://images.unsplash.com/photo-1465311530779-5241f5a29892?auto=format&fit=crop&q=80&w=800'
}, {
  id: '6',
  name: 'Sunset Cove',
  type: 'coast',
  rating: 4.6,
  distance: '18 mi',
  fishTypes: ['Halibut', 'Cod'],
  description: 'Sheltered cove perfect for kayak fishing.',
  regulations: 'Permit required',
  image: 'https://images.unsplash.com/photo-1534008897995-27a23e859048?auto=format&fit=crop&q=80&w=800'
}];
interface FishingMapProps {
  searchQuery?: string;
}
export function FishingMap({
  searchQuery = ''
}: FishingMapProps) {
  const {
    activeLocationId,
    setActiveLocationId,
    favorites
  } = useApp();
  const [filters, setFilters] = useState<FilterState>({
    species: [],
    waterType: [],
    favoritesOnly: false
  });
  const filteredLocations = LOCATIONS.filter(location => {
    if (searchQuery && !location.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filters.species.length > 0) {
      const hasSpecies = location.fishTypes.some(fish => filters.species.some(s => fish.includes(s)));
      if (!hasSpecies) return false;
    }
    if (filters.waterType.length > 0) {
      if (!filters.waterType.map(t => t.toLowerCase()).includes(location.type)) {
        return false;
      }
    }
    if (filters.favoritesOnly && !favorites.includes(location.id)) {
      return false;
    }
    return true;
  });
  const selectedLocation = LOCATIONS.find(l => l.id === activeLocationId);
  const coords: Record<string, {
    x: number;
    y: number;
  }> = {
    '1': {
      x: 30,
      y: 40
    },
    '2': {
      x: 65,
      y: 25
    },
    '3': {
      x: 80,
      y: 70
    },
    '4': {
      x: 20,
      y: 75
    },
    '5': {
      x: 45,
      y: 15
    },
    '6': {
      x: 85,
      y: 55
    }
  };
  return <div className="relative w-full h-full water-bg overflow-hidden" data-id="element-70">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-30" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 30 Q15 20 30 30 T60 30' fill='none' stroke='%230d9488' stroke-opacity='0.1' stroke-width='1'/%3E%3C/svg%3E")`,
      backgroundSize: '60px 60px'
    }} data-id="element-71"></div>

      <FilterPanel filters={filters} onFilterChange={setFilters} data-id="element-72" />
      <WeatherWidget data-id="element-73" />

      {/* Markers */}
      <div className="absolute inset-0" data-id="element-74">
        {filteredLocations.map(location => {
        const coord = coords[location.id] || {
          x: 50,
          y: 50
        };
        return <LocationMarker key={location.id} x={coord.x} y={coord.y} type={location.type} isSelected={activeLocationId === location.id} onClick={() => setActiveLocationId(location.id)} locationId={location.id} data-id="element-75" />;
      })}
      </div>

      {/* Location Card */}
      <AnimatePresence data-id="element-76">
        {selectedLocation && <LocationCard location={selectedLocation} onClose={() => setActiveLocationId(null)} data-id="element-77" />}
      </AnimatePresence>

      {/* Results count */}
      <div className="absolute bottom-20 md:bottom-4 left-4 text-xs text-[var(--text-muted)] bg-[var(--bg-card)]/80 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-78">
        {filteredLocations.length} spots
      </div>
    </div>;
}